import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, Chip, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Stack, 
  Card,
  CardContent,
  LinearProgress,
  Paper,
  Grid,
  Avatar
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Bolt, CheckCircle, Warning } from "@mui/icons-material";
import { SuccessIcon } from "src/theme/overrides/CustomIcons";
import { getColorByKey } from "../../common/util";
import Iconify from "src/components/Iconify";

type Props = {
  organizationName: string;
  open: boolean;
  onClose: () => void;
  data: {
    message,
    totals: {
      totalFhir: number;
      successFhir: number;
      failedFhir: number;
      workflow: number;
    },
    details: {
      successDetail: any[],
      failedDetail: any[],
      workflowDetail: any[]
    }
  } | null;
  translate: any;
};

export default function OrganizationResultDialog({ 
  open, 
  onClose, 
  organizationName, 
  data, 
  translate 
}: Props) {

  const { totals, details } = data ?? {};

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {`${organizationName} ${translate('details.activationFlow.dialogResult.title')}`}
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} mb={6}>
          <Grid item xs={12} md={4}>
            <SummaryCard
              icon={ 
                <CircleIcon backgroundColor="#e3f2fd">
                  <Iconify
                    icon="grommet-icons:resources"
                    color="#2196f3"
                    height={24}
                    width={24}
                  />
                </CircleIcon>
              }
              title={translate('details.activationFlow.dialogResult.fhir')}
              value={`${totals?.successFhir ?? 0} / ${totals?.totalFhir ?? 0}`}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <SummaryCard
              icon={
                <CircleIcon backgroundColor="#fffde7">
                  <Iconify 
                    icon="hugeicons:workflow-circle-06" 
                    color="#f9a825"
                    height={24}
                    width={24}
                  />
                </CircleIcon>
              }
              title={translate('details.activationFlow.dialogResult.workflow')}
              value={totals?.workflow ?? 0}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <SummaryCard
              icon={
              <CircleIcon backgroundColor="#fbe9e7">
                <Warning color="error" />
              </CircleIcon>
              }
              title={translate('details.activationFlow.dialogResult.failed')}
              value={totals?.failedFhir ?? 0}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          {details?.successDetail?.length &&
            <Grid item xs={12}>
              <Typography variant="h5" fontWeight={800} mb={2}>
                {translate('details.activationFlow.dialogResult.fhirDetail')}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 1.5
                }}
              >
                {details?.successDetail?.map((item, index) => (
                  <ListItem 
                    key={item.name} 
                    index={index}
                    {...item}
                    icon="grommet-icons:resources"
                  />
                ))}
              </Box>
            </Grid>
          }

          {details?.workflowDetail?.length && 
            <Grid item xs={12}>
              <Typography variant="h5" fontWeight={800} mb={2}>
                {translate('details.activationFlow.dialogResult.workflowDetail')}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 1.5
                }}
              >
                {details?.workflowDetail?.map((item, index) => (
                  <ListItem
                    key={item.name}
                    index={index}
                    {...item}
                    icon="hugeicons:workflow-circle-06"
                  />
                ))}
              </Box>
            </Grid>
          }
          
          <Grid item xs={12}>
            {details?.failedDetail && details?.failedDetail?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    {translate('details.activationFlow.dialogResult.failedTitle')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {details?.failedDetail?.map((f, i) => (
                      <Box key={i} p={1} border="1px solid #eee" borderRadius={2}>
                        <Typography variant="body2" fontWeight="bold">
                          {f.resource.resource}
                        </Typography>
                        <Typography variant="caption" color="error">
                          {f.error.message}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const SummaryCard = ({ icon, title, value }: any) => (
  <Card sx={{ borderRadius: 3 }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Box>{icon}</Box>
      </Stack>

      <Typography variant="overline">{title}</Typography>
      <Typography variant="h4" fontWeight={800}>
        {value}
      </Typography>

      <LinearProgress sx={{ mt: 2, borderRadius: 2 }} value={100} variant="determinate" />
    </CardContent>
  </Card>
);

const CircleIcon = ({ children, backgroundColor }) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: backgroundColor,
    }}
  >
    {children}
  </Box>
);

const ListItem = ({ name, count, index, icon }) => (
  <Paper
    key={`${name}-00TY${index}`}
    elevation={1}
    sx={{
      p: 2.5,
      borderRadius: 2,
      display: "flex",
      alignItems: "center",
      gap: 2,
    }}
  >
    <Avatar
      sx={{
        width: 48,
        height: 48,
        bgcolor: getColorByKey(name),
        color: "#ffff",
      }}
    >
      <Iconify icon={icon} />
    </Avatar>

    <Box>
      <Typography variant="h5" fontWeight={700}>
        {count}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {name}
      </Typography>
    </Box>
  </Paper>
);